import API_BASE_URL from '../config/apiConfig';
import { apiClient } from './authService';

/**
 * Submit a maintenance request for a tenant's unit.
 * Endpoint: POST /api/properties/{propertyId}/units/{unitId}/maintenance-requests
 */
export const submitMaintenanceRequest = async (propertyId, unitId, { description }) => {
  try {
    const safePropertyId = String(propertyId || '').trim();
    const safeUnitId = String(unitId || '').trim();
    const safeDescription = String(description || '').trim();

    if (!safePropertyId || !safeUnitId) {
      throw new Error('Property and unit are required to submit a maintenance request.');
    }
    if (!safeDescription) {
      throw new Error('Description is required.');
    }

    const response = await apiClient(
      `${API_BASE_URL}/api/properties/${safePropertyId}/units/${safeUnitId}/maintenance-requests`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ description: safeDescription }),
      },
      true
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data?.message || data?.error || 'Failed to submit maintenance request';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('submitMaintenanceRequest error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * List authenticated tenant maintenance requests.
 * Endpoint: GET /api/maintenance-requests/my
 */
export const getMyMaintenanceRequests = async () => {
  try {
    const response = await apiClient(
      `${API_BASE_URL}/api/maintenance-requests/my`,
      { method: 'GET' },
      true
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data?.message || data?.error || 'Failed to load maintenance requests';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('getMyMaintenanceRequests error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * List maintenance requests for a property (landlord/agent).
 * Endpoint: GET /api/properties/{propertyId}/maintenance-requests
 */
export const getPropertyMaintenanceRequests = async (propertyId) => {
  try {
    const safePropertyId = String(propertyId || '').trim();
    if (!safePropertyId) {
      throw new Error('Property is required.');
    }

    const response = await apiClient(
      `${API_BASE_URL}/api/properties/${safePropertyId}/maintenance-requests`,
      { method: 'GET' },
      true
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data?.message || data?.error || 'Failed to load property maintenance requests';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('getPropertyMaintenanceRequests error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * List maintenance requests for a specific property unit (landlord/agent).
 * Endpoint: GET /api/properties/{propertyId}/units/{unitId}/maintenance-requests
 */
export const getUnitMaintenanceRequests = async (propertyId, unitId) => {
  try {
    const safePropertyId = String(propertyId || '').trim();
    const safeUnitId = String(unitId || '').trim();
    if (!safePropertyId || !safeUnitId) {
      throw new Error('Property and unit are required.');
    }

    const response = await apiClient(
      `${API_BASE_URL}/api/properties/${safePropertyId}/units/${safeUnitId}/maintenance-requests`,
      { method: 'GET' },
      true
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data?.message || data?.error || 'Failed to load unit maintenance requests';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('getUnitMaintenanceRequests error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Update a maintenance request status/notes (landlord/agent manager).
 * Endpoint: PUT /api/maintenance-requests/{requestId}
 * @param {string} requestId
 * @param {{ status: 'OPEN'|'HANDLED'|'COMPLETED'|'CANCELLED', landlordNotes?: string }} payload
 */
export const updateMaintenanceRequest = async (requestId, { status, landlordNotes } = {}) => {
  try {
    const safeRequestId = String(requestId || '').trim();
    const safeStatus = String(status || '').trim().toUpperCase();

    if (!safeRequestId) {
      throw new Error('Maintenance request is required.');
    }
    if (!safeStatus) {
      throw new Error('Status is required.');
    }

    const body = { status: safeStatus };
    if (landlordNotes != null) {
      const notes = String(landlordNotes).trim();
      body.landlordNotes = notes;
    }

    const response = await apiClient(
      `${API_BASE_URL}/api/maintenance-requests/${safeRequestId}`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
      true
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data?.message || data?.error || 'Failed to update maintenance request';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('updateMaintenanceRequest error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};
